import axios, { AxiosInstance } from 'axios';
import { 
  Etablissement, 
  Photo, 
  PhotoCategorie, 
  AirtableResponse, 
  SearchFilters,
  ApiError,
  CloudinaryMetadata
} from '../types';

// Configuration de l'API Airtable
const AIRTABLE_BASE_URL = `https://api.airtable.com/v0/${import.meta.env.VITE_AIRTABLE_BASE_ID}`;
const AIRTABLE_API_KEY = import.meta.env.VITE_AIRTABLE_API_KEY;
const FICHES_PPMS_TABLE = import.meta.env.VITE_AIRTABLE_FICHES_PPMS_TABLE;
const PHOTOS_TABLE = import.meta.env.VITE_AIRTABLE_PHOTOS_TABLE;

// Log pour déboguer les variables d'environnement
console.log('=== Variables d\'environnement Airtable ===');
console.log('AIRTABLE_BASE_URL:', AIRTABLE_BASE_URL);
console.log('AIRTABLE_API_KEY:', AIRTABLE_API_KEY ? '***DÉFINIE***' : 'NON DÉFINIE');
console.log('FICHES_PPMS_TABLE:', FICHES_PPMS_TABLE);
console.log('PHOTOS_TABLE:', PHOTOS_TABLE);
console.log('==========================================');

class AirtableService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: AIRTABLE_BASE_URL,
      headers: {
        'Authorization': `Bearer ${AIRTABLE_API_KEY}`,
        'Content-Type': 'application/json',
      },
    });

    // Intercepteur pour gérer les erreurs
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        const apiError: ApiError = {
          message: error.response?.data?.error?.message || 'Erreur de communication avec Airtable',
          code: error.response?.status?.toString(),
          details: error.response?.data
        };
        return Promise.reject(apiError);
      }
    );
  }

  // Récupérer tous les établissements
  async getEtablissements(filters?: SearchFilters): Promise<Etablissement[]> {
    try {
      let formula = '';
      const filterArray: string[] = [];

      if (filters?.departement) {
        filterArray.push(`{Departement} = '${filters.departement}'`);
      }

      if (filterArray.length > 0) {
        formula = `AND(${filterArray.join(', ')})`;
      }

      const params: any = {
        view: 'Grid view',
      };

      if (formula) {
        params.filterByFormula = formula;
      }

      const response = await this.api.get<AirtableResponse<any>>(
        `/${FICHES_PPMS_TABLE}`,
        { params }
      );

      return response.data.records.map(record => ({
        id: record.id,
        submissionId: record.fields['Submission ID'] || '',
        nom: record.fields.Nom_Etablissement || '',
        type: record.fields.Type_Etablissement || '',
        departement: record.fields.Département || '',
        adresse: record.fields.Adresse || '',
        codePostal: record.fields.Code_Postal || '',
        commune: record.fields.Commune || '',
        academie: record.fields.Académie || '',
        submissionTime: record.fields.Submission_time || record.createdTime,
      }));
    } catch (error) {
      console.error('Erreur lors de la récupération des établissements:', error);
      throw error;
    }
  }

  // Récupérer un établissement par ID
  async getEtablissement(id: string): Promise<Etablissement> {
    try {
      const response = await this.api.get(`/${FICHES_PPMS_TABLE}/${id}`);
      const record = response.data;
      
      return {
        id: record.id,
        submissionId: record.fields['Submission ID'] || '',
        nom: record.fields.Nom_Etablissement || '',
        type: record.fields.Type_Etablissement || '',
        departement: record.fields.Département || '',
        adresse: record.fields.Adresse || '',
        codePostal: record.fields.Code_Postal || '',
        commune: record.fields.Commune || '',
        academie: record.fields.Académie || '',
        submissionTime: record.fields.Submission_time || record.createdTime,
      };
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'établissement:', error);
      throw error;
    }
  }

  // Récupérer les photos d'un établissement
  async getPhotosEtablissement(etablissementId: string): Promise<Photo[]> {
    try {
      console.log('Récupération des photos pour Submission ID :', etablissementId);
      const response = await this.api.get<AirtableResponse<any>>(
        `/${PHOTOS_TABLE}`,
        {
          params: {
            filterByFormula: `{Submission ID} = '${etablissementId}'`,
            view: 'Grid view',
          }
        }
      );

      return response.data.records.map(record => ({
        id: record.id,
        etablissementId: record.fields['Submission ID'] || '',
        categorie: record.fields.Categorie as PhotoCategorie || PhotoCategorie.FACADE,
        sousCategorie: record.fields['Sous-categorie'] || undefined,
        legende: record.fields.Legende || '',
        description: record.fields.Description || undefined,
        photoUrl: record.fields.Photo?.[0]?.url || '',
        createdAt: record.createdTime,
        updatedAt: record.fields.updated_at || record.createdTime,
        photoMetadata: record.fields['Photo Metadata'] ? JSON.parse(record.fields['Photo Metadata'] as string) : undefined,
      }));
    } catch (error) {
      console.error('Erreur lors de la récupération des photos:', error);
      throw error;
    }
  }

  // Ajouter une nouvelle photo
  async addPhoto(photoData: Omit<Photo, 'id' | 'createdAt' | 'updatedAt'>): Promise<Photo> {
    try {
      const response = await this.api.post(`/${PHOTOS_TABLE}`, {
        fields: {
          'Submission ID': photoData.etablissementId,
          'Categorie': photoData.categorie,
          'Sous-categorie': photoData.sousCategorie || '',
          'Legende': photoData.legende,
          'Description': photoData.description || '',
          'Photo': photoData.photoUrl ? [{ url: photoData.photoUrl }] : [],
          'Photo Metadata': photoData.photoMetadata ? JSON.stringify(photoData.photoMetadata) : '',
          'Date': new Date().toISOString(),
        }
      });

      const record = response.data;
      return {
        id: record.id,
        etablissementId: record.fields['Submission ID'] || '',
        categorie: record.fields.Categorie as PhotoCategorie || PhotoCategorie.FACADE,
        sousCategorie: record.fields['Sous-categorie'] || undefined,
        legende: record.fields.Legende || '',
        description: record.fields.Description || undefined,
        photoUrl: record.fields.Photo?.[0]?.url || '',
        createdAt: record.createdTime,
        updatedAt: record.createdTime,
        photoMetadata: record.fields['Photo Metadata'] ? JSON.parse(record.fields['Photo Metadata'] as string) : undefined,
      };
    } catch (error) {
      console.error('Erreur lors de l\'ajout de la photo:', error);
      throw error;
    }
  }

  // Ajouter une photo avec métadonnées Cloudinary
  async addPhotoWithMetadata(
    photoData: Omit<Photo, 'id' | 'createdAt' | 'updatedAt' | 'photoMetadata'>,
    cloudinaryMetadata: CloudinaryMetadata
  ): Promise<Photo> {
    try {
      const response = await this.api.post(`/${PHOTOS_TABLE}`, {
        fields: {
          'Submission ID': photoData.etablissementId,
          'Categorie': photoData.categorie,
          'Sous-categorie': photoData.sousCategorie || '',
          'Legende': photoData.legende,
          'Description': photoData.description || '',
          'Photo': photoData.photoUrl ? [{ url: photoData.photoUrl }] : [],
          'Photo Metadata': JSON.stringify(cloudinaryMetadata),
          'Date': new Date().toISOString(),
        }
      });

      const record = response.data;
      return {
        id: record.id,
        etablissementId: record.fields['Submission ID'] || '',
        categorie: record.fields.Categorie as PhotoCategorie || PhotoCategorie.FACADE,
        sousCategorie: record.fields['Sous-categorie'] || undefined,
        legende: record.fields.Legende || '',
        description: record.fields.Description || undefined,
        photoUrl: record.fields.Photo?.[0]?.url || '',
        photoMetadata: cloudinaryMetadata,
        createdAt: record.createdTime,
        updatedAt: record.createdTime,
      };
    } catch (error) {
      console.error('Erreur lors de l\'ajout de la photo avec métadonnées:', error);
      throw error;
    }
  }

  // Mettre à jour une photo
  async updatePhoto(id: string, photoData: Partial<Photo>): Promise<Photo> {
    try {
      const fields: any = {};
      
      if (photoData.categorie !== undefined) fields.Categorie = photoData.categorie;
      if (photoData.sousCategorie !== undefined) fields['Sous-categorie'] = photoData.sousCategorie;
      if (photoData.legende !== undefined) fields.Legende = photoData.legende;
      if (photoData.description !== undefined) fields.Description = photoData.description;
      if (photoData.photoUrl !== undefined) fields.Photo = photoData.photoUrl ? [{ url: photoData.photoUrl }] : [];
      if (photoData.photoMetadata !== undefined) fields['Photo Metadata'] = JSON.stringify(photoData.photoMetadata);

      const response = await this.api.patch(`/${PHOTOS_TABLE}/${id}`, {
        fields
      });

      const record = response.data;
      return {
        id: record.id,
        etablissementId: record.fields['Submission ID'] || '',
        categorie: record.fields.Categorie as PhotoCategorie || PhotoCategorie.FACADE,
        sousCategorie: record.fields['Sous-categorie'] || undefined,
        legende: record.fields.Legende || '',
        description: record.fields.Description || undefined,
        photoUrl: record.fields.Photo?.[0]?.url || '',
        createdAt: record.createdTime,
        updatedAt: new Date().toISOString(),
        photoMetadata: record.fields['Photo Metadata'] ? JSON.parse(record.fields['Photo Metadata'] as string) : undefined,
      };
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la photo:', error);
      throw error;
    }
  }

  // Supprimer une photo
  async deletePhoto(id: string): Promise<void> {
    try {
      await this.api.delete(`/${PHOTOS_TABLE}/${id}`);
    } catch (error) {
      console.error('Erreur lors de la suppression de la photo:', error);
      throw error;
    }
  }

  // Récupérer les options pour les formulaires
  async getFormOptions(): Promise<{
    departements: string[];
    typesEtablissement: string[];
    academies: string[];
  }> {
    try {
      const [departements] = await Promise.all([
        this.getUniqueValues('Departement'),
      ]);

      return {
        departements,
        typesEtablissement: [],
        academies: [],
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des options:', error);
      throw error;
    }
  }

  // Méthode utilitaire pour récupérer des valeurs uniques
  private async getUniqueValues(fieldName: string): Promise<string[]> {
    try {
      const response = await this.api.get<AirtableResponse<any>>(
        `/${FICHES_PPMS_TABLE}`,
        {
          params: {
            view: 'Grid view',
            fields: [fieldName === 'Département' ? 'Departement' : fieldName],
          }
        }
      );

      const values = response.data.records
        .map(record => record.fields[fieldName])
        .filter((value, index, self) => value && self.indexOf(value) === index)
        .sort();

      return values;
    } catch (error) {
      console.error(`Erreur lors de la récupération des valeurs uniques pour ${fieldName}:`, error);
      return [];
    }
  }
}

export const airtableService = new AirtableService(); 